import * as React from 'react';
import {
    Card,
    CardActionArea,
    CardContent,
    Typography,
    CardActions,
    Button,
    Box,
} from '@mui/material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import { Link } from 'react-router-dom';

const QualityLink = React.forwardRef((props: any, ref: any) => (
    <Link to="/quality" ref={ref} {...props} />
));

interface StatusCardProps {
    ok: boolean | null;
    title: string;
    eyebrow?: string;
    failCount?: number | null;
    iconComponent?: any;
    description: React.ReactNode;
}

class StatusCard extends React.PureComponent<StatusCardProps> {
    render() {
        const { ok, title, iconComponent, description, eyebrow, failCount } = this.props;
        const Icon = iconComponent || AccessAlarmIcon;

        const status =
            ok == null ? 'idle' :
            ok ? 'ok' : 'warn';

        const tone = STATUS_TONE[status];

        return (
            <Card
                sx={{
                    height: '100%',
                    minHeight: 200,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Tone-aware ambient wash. */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: tone.bg,
                        opacity: 0.9,
                        pointerEvents: 'none',
                    }}
                />
                <CardActionArea
                    component={QualityLink}
                    nativeButton={false}
                    sx={{ position: 'relative', alignItems: 'stretch', height: '100%' }}
                >
                    <CardContent sx={{ position: 'relative', p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '14px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: tone.iconBg,
                                    color: tone.iconColor,
                                    border: `1px solid ${tone.iconBorder}`,
                                    boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset',
                                }}
                            >
                                <Icon style={{ width: 22, height: 22, fill: 'currentColor' }} />
                            </Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    paddingInline: 1.1,
                                    paddingBlock: 0.4,
                                    borderRadius: 999,
                                    border: `1px solid ${tone.chipBorder}`,
                                    background: tone.chipBg,
                                    color: tone.chipColor,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 10.5,
                                    fontWeight: 500,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        background: tone.dot,
                                        boxShadow: `0 0 0 3px ${tone.dotHalo}`,
                                    }}
                                    aria-hidden
                                />
                                {tone.label}
                            </Box>
                        </Box>

                        {eyebrow && (
                            <Typography
                                className="eyebrow"
                                sx={{ mt: 2.5, color: 'text.secondary' }}
                            >
                                {eyebrow}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mt: 0.5 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Inter Tight", "Inter", sans-serif',
                                    fontWeight: 700,
                                    letterSpacing: '-0.014em',
                                    fontSize: 22,
                                    color: 'text.primary',
                                }}
                            >
                                {title}
                            </Typography>
                            {typeof failCount === 'number' && failCount > 0 && (
                                <Typography
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: tone.countColor,
                                    }}
                                >
                                    · {failCount} fail{failCount === 1 ? '' : 's'}
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.55 }}
                        >
                            {description}
                        </Typography>
                    </CardContent>
                </CardActionArea>
                <CardActions sx={{ position: 'relative', px: 2, pb: 2 }}>
                    <Button
                        disabled={ok !== false}
                        component={QualityLink}
                        nativeButton={false}
                        size="small"
                        variant="text"
                        sx={{
                            color: ok === false ? 'secondary.dark' : 'text.disabled',
                            fontWeight: 600,
                        }}
                    >
                        View failed →
                    </Button>
                </CardActions>
            </Card>
        );
    }
}

type Tone = {
    label: string;
    bg: string;
    iconBg: string;
    iconColor: string;
    iconBorder: string;
    chipBg: string;
    chipBorder: string;
    chipColor: string;
    dot: string;
    dotHalo: string;
    countColor: string;
};

const STATUS_TONE: Record<'idle' | 'ok' | 'warn', Tone> = {
    idle: {
        label: 'Loading',
        bg: 'radial-gradient(600px 200px at 0% 0%, rgba(11, 18, 48, 0.04), transparent 60%)',
        iconBg: 'linear-gradient(180deg, rgba(11, 18, 48, 0.06) 0%, rgba(11, 18, 48, 0.02) 100%)',
        iconBorder: 'rgba(11, 18, 48, 0.10)',
        iconColor: 'rgba(11, 18, 48, 0.55)',
        chipBg: 'rgba(11, 18, 48, 0.05)',
        chipBorder: 'rgba(11, 18, 48, 0.10)',
        chipColor: 'rgba(11, 18, 48, 0.55)',
        dot: '#a8aec5',
        dotHalo: 'rgba(168, 174, 197, 0.20)',
        countColor: 'rgba(11, 18, 48, 0.55)',
    },
    ok: {
        label: 'Healthy',
        bg: 'radial-gradient(600px 220px at 100% 0%, rgba(54, 200, 130, 0.10), transparent 60%)',
        iconBg: 'linear-gradient(180deg, rgba(54, 200, 130, 0.16) 0%, rgba(54, 200, 130, 0.06) 100%)',
        iconBorder: 'rgba(54, 200, 130, 0.32)',
        iconColor: '#1f7a4d',
        chipBg: 'rgba(54, 200, 130, 0.12)',
        chipBorder: 'rgba(54, 200, 130, 0.32)',
        chipColor: '#1f7a4d',
        dot: '#36c882',
        dotHalo: 'rgba(54, 200, 130, 0.24)',
        countColor: '#1f7a4d',
    },
    warn: {
        label: 'Action needed',
        bg: 'radial-gradient(600px 220px at 100% 0%, rgba(250, 135, 28, 0.16), transparent 60%)',
        iconBg: 'linear-gradient(180deg, rgba(250, 135, 28, 0.20) 0%, rgba(250, 135, 28, 0.06) 100%)',
        iconBorder: 'rgba(250, 135, 28, 0.36)',
        iconColor: '#a85b08',
        chipBg: 'rgba(250, 135, 28, 0.12)',
        chipBorder: 'rgba(250, 135, 28, 0.36)',
        chipColor: '#a85b08',
        dot: '#FA871C',
        dotHalo: 'rgba(250, 135, 28, 0.30)',
        countColor: '#a85b08',
    },
};

export default StatusCard;
